import { FormEvent, useMemo, useRef, useState } from 'react';

import type { FilterModel, GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/hooks';
import { AccountAutoComplete } from '@/components/common/account-auto-complete';
import { WithLocalStorage } from '@/components/hoc';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, lookupSideBar, sideBar } from '@/grid/defaults';
import { ColDef, getRowId } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { AccountResponse } from '@/services/accounts';
import { AccountAutoCompleteItem } from '@/services/lookups/types';

import { useAccountLookupColumnDefs } from './column-defs';
import { useAdvancedAccountDataSource } from './useAdvancedAccountDataSource';

type AdvancedAccountLookupProps = {
  className?: string;
  container?: React.ComponentProps<typeof DialogContent>['container'];
  label?: string;
  value?: AccountResponse | null;
  onChange?: (account: AccountResponse) => void;
  disabled?: boolean;
  tabIndex?: number;
};

const DATA_TABLE_ROWS = 50;
const CACHE_BLOCK_SIZE = 500;
const LOAD_DEBOUNCE_MS = 500;

type SearchByField =
  | ''
  | 'name'
  | 'code'
  | 'contactName'
  | 'phoneNumber'
  | 'ranchAddress'
  | 'billingAddress';
type AccountGridApi = GridApi<AccountResponse>;

const SEARCH_BY_OPTIONS: { label: string; value: SearchByField }[] = [
  { label: 'All', value: '' },
  { label: 'Account Name', value: 'name' },
  { label: 'Ranch ID', value: 'code' },
];

const ALTERNATE_SEARCH_BY_OPTIONS: { label: string; value: SearchByField }[] = [
  { label: 'Phone Number', value: 'phoneNumber' },
  { label: 'Ranch Address', value: 'ranchAddress' },
  { label: 'Billing Address', value: 'billingAddress' },
];

const ACTIVE_STATE_CODE = 0;

function getStatusFilterValues(filterModel: FilterModel) {
  const values = filterModel.stateCode?.values;

  if (!Array.isArray(values)) {
    return null;
  }

  return values.map(Number).filter((value) => Number.isFinite(value));
}

function isActiveOnlyStatusFilter(values: number[] | null) {
  return values?.length === 1 && values[0] === ACTIVE_STATE_CODE;
}

export const AdvancedAccountLookup = ({
  className,
  container,
  value,
  label,
  onChange,
  disabled,
  tabIndex,
}: AdvancedAccountLookupProps) => {
  const { t } = useTranslation();
  const defaultGridOptions = useDefaultGridOptions();
  const mode = useAppSelector((state) => state.preferences.mode);
  const isAdvancedAccountSearchEnabled = useFeatureFlag(
    'DS_Account_Advanced_Search',
  );
  const isSpecificViewContextEnabled = useFeatureFlag('Specific_View_Context');
  const isAlternateSearchEnabled = useFeatureFlag('Alternate_Account_Lookup');

  const searchByOptions = useMemo(
    () =>
      isAlternateSearchEnabled
        ? [...SEARCH_BY_OPTIONS, ...ALTERNATE_SEARCH_BY_OPTIONS]
        : SEARCH_BY_OPTIONS,
    [isAlternateSearchEnabled],
  );

  const gridRef = useRef<AgGridReact<AccountResponse>>(null);
  const valueName = value?.name || '';

  const [dialogVisible, setDialogVisible] = useState(false);
  const [searchTermTemp, setSearchTermTemp] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSearchBy, setSelectedSearchBy] = useState<SearchByField>('');
  const [selectedAccount, setSelectedAccount] =
    useState<AccountResponse | null>(null);
  const [hideInactive, setHideInactive] = useState(true);

  const { dataSource } = useAdvancedAccountDataSource({
    searchBy: selectedSearchBy,
    searchTerm,
    hideInactive,
  });
  const activeColumnDefs = useAccountLookupColumnDefs();
  const shouldRestorePersistedFilters =
    isAlternateSearchEnabled || isAdvancedAccountSearchEnabled;

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSearchTerm(searchTermTemp);
  };

  const handleSelectAccount = () => {
    if (selectedAccount) {
      onChange?.(selectedAccount);
      setDialogVisible(false);
    }
  };

  const handleAutocompleteSelect = (e: { value: AccountAutoCompleteItem }) => {
    onChange?.(e.value as AccountResponse);
  };

  const handleSelectionChanged = (e: { api: AgGridReact['api'] }) => {
    const selectedNode = e.api.getSelectedNodes()[0];
    if (selectedNode?.data) {
      setSelectedAccount(selectedNode.data);
    } else {
      setSelectedAccount(null);
    }
  };

  const handleAutocompleteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isAlternateSearchEnabled) {
      e.preventDefault();
      const inputText = (e.target as HTMLInputElement).value || valueName;
      setSearchTermTemp(inputText);
      setSearchTerm(inputText);
      setSelectedAccount(null);
      setDialogVisible(true);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isAlternateSearchEnabled) {
      e.preventDefault();
      setSearchTerm(searchTermTemp);
    }
  };

  const handleOpenDialog = () => {
    setSearchTermTemp(valueName);
    setSearchTerm(valueName);
    setSelectedAccount(null);
    setDialogVisible(true);
  };

  const setStatusFilter = (
    values: number[] | null,
    api = gridRef.current?.api as AccountGridApi | undefined,
  ) => {
    if (!api) return;

    const filterModel = api.getFilterModel();
    const currentValues = getStatusFilterValues(filterModel);
    const sameValues =
      currentValues?.length === values?.length &&
      currentValues?.every((value, index) => value === values?.[index]);

    if (sameValues || (!currentValues && !values)) {
      return;
    }

    const nextFilterModel = { ...filterModel };

    if (values) {
      nextFilterModel.stateCode = {
        filterType: 'set',
        values: values.map(String),
      };
    } else {
      delete nextFilterModel.stateCode;
    }

    api.setFilterModel(nextFilterModel);
  };

  const syncHideInactiveFromStatusFilter = () => {
    const api = gridRef.current?.api;
    if (!api) return;

    setHideInactive(
      isActiveOnlyStatusFilter(getStatusFilterValues(api.getFilterModel())),
    );
  };

  const reconcileStatusFilterAfterLayoutLoad = (
    api = gridRef.current?.api as AccountGridApi | undefined,
  ) => {
    if (!api) return;

    const statusValues = getStatusFilterValues(api.getFilterModel());

    if (statusValues) {
      setHideInactive(isActiveOnlyStatusFilter(statusValues));
      return;
    }

    if (hideInactive) {
      setStatusFilter([ACTIVE_STATE_CODE], api);
    }
  };

  const handleHideInactiveChange = (checked: boolean | 'indeterminate') => {
    const nextHideInactive = checked === true;
    setHideInactive(nextHideInactive);
    setStatusFilter(nextHideInactive ? [ACTIVE_STATE_CODE] : null);
  };

  return (
    <div className="label-input-group col-span-2 w-full">
      {label && <label>{label}</label>}
      <div className="grid w-full grid-cols-[minmax(0,1fr)_2.357rem] items-stretch gap-x-2">
        <AccountAutoComplete
          className={`w-full ${className ?? ''}`.trim()}
          defaultValue={
            value
              ? (value as unknown as string & AccountAutoCompleteItem)
              : undefined
          }
          field="name"
          disabled={disabled}
          onSelect={handleAutocompleteSelect}
          onKeyDown={handleAutocompleteKeyDown}
          tabIndex={tabIndex}
        />
        <Button
          className="h-12 w-full min-w-0 self-center rounded-sm"
          size="icon-lg"
          onClick={handleOpenDialog}
          type="button"
          disabled={disabled}
          aria-label={t('account.searchAccount')}
        >
          <i className="pi pi-search text-base" />
        </Button>
      </div>

      <Dialog open={dialogVisible} onOpenChange={setDialogVisible}>
        <DialogContent
          container={container}
          className="gap-0 bg-[var(--surface-c)] px-0 pt-0 pb-4 text-[var(--text-color)] md:h-[90vh] md:max-h-[90vh] md:max-w-[75vw]"
        >
          <DialogHeader className="px-6 py-5">
            <DialogTitle className="text-xl font-semibold">
              {t('account.accountLookup')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="grid grid-rows-[min-content_1fr] gap-y-4 overflow-hidden px-4 py-0">
            <Card className="m-2 w-80">
              <CardContent className="flex flex-col gap-4 self-start px-4 pt-4 pb-2">
                <form
                  className="label-input-column w-full"
                  onSubmit={handleSearch}
                >
                  <div className="flex flex-col gap-3">
                    <div className="label-input-group">
                      <label>{t('common.searchBy')}</label>
                      <Select
                        value={selectedSearchBy}
                        onChange={(v) =>
                          setSelectedSearchBy(v as SearchByField)
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue>
                            {searchByOptions.find(
                              (o) => o.value === selectedSearchBy,
                            )?.label ?? 'All'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {searchByOptions.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="label-input-group">
                      <label>{t('common.search')}</label>
                      <div className="grid grid-cols-[auto_min-content] items-center gap-x-2">
                        <Input
                          value={searchTermTemp}
                          onChange={(e) => setSearchTermTemp(e.target.value)}
                          onKeyDown={handleSearchKeyDown}
                          data-skip-enter-advance
                          disabled={disabled}
                        />
                        <Button
                          size="icon"
                          type="submit"
                          disabled={disabled}
                          aria-label={t('common.search')}
                        >
                          <i className="pi pi-search text-base" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={hideInactive}
                        onCheckedChange={handleHideInactiveChange}
                        id="hide-inactive"
                      />
                      <label htmlFor="hide-inactive">
                        {t('account.hideInactiveAccounts')}
                      </label>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
            <div className="m-2 min-h-0">
              <WithLocalStorage
                storageKey="account-lookup"
                onLoad={
                  shouldRestorePersistedFilters
                    ? (api) =>
                        reconcileStatusFilterAfterLayoutLoad(
                          api as AccountGridApi,
                        )
                    : undefined
                }
              >
                <AgGridReact
                  gridOptions={defaultGridOptions}
                  className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''} h-full`}
                  ref={gridRef}
                  defaultColDef={{
                    ...(defaultColDef as ColDef<AccountResponse>),
                    filter: true,
                    filterParams: {
                      buttons: ['apply', 'clear'],
                      closeOnApply: true,
                    },
                  }}
                  columnDefs={activeColumnDefs}
                  getRowId={getRowId<AccountResponse>((params) =>
                    String(params.data.id),
                  )}
                  rowModelType="serverSide"
                  rowSelection="single"
                  cacheBlockSize={CACHE_BLOCK_SIZE}
                  blockLoadDebounceMillis={LOAD_DEBOUNCE_MS}
                  serverSideDatasource={dataSource ?? undefined}
                  paginationPageSize={DATA_TABLE_ROWS}
                  onFilterChanged={syncHideInactiveFromStatusFilter}
                  onSelectionChanged={handleSelectionChanged}
                  onCellDoubleClicked={handleSelectAccount}
                  sideBar={
                    isSpecificViewContextEnabled
                      ? lookupSideBar('advanced-account-lookup')
                      : sideBar
                  }
                />
              </WithLocalStorage>
            </div>
          </DialogBody>
          <DialogFooter className="mx-0 mb-0 flex-row justify-end border-t-0 bg-transparent px-4 pt-4 pb-0">
            <Button
              variant="outline"
              onClick={() => setDialogVisible(false)}
              type="button"
            >
              <i className="pi pi-times" />
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSelectAccount}
              disabled={selectedAccount === null}
              type="button"
            >
              <i className="pi pi-check" />
              {t('common.select')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
